import React, { useEffect, useRef, useState } from "react";
import SvgIcon from "@/components/ui/svg-icons";
import BottomSheet from "@/components/ui/bottomsheet";
import { pl } from "@/components/ui/typography/typography.module.css";
import { chaptersLinkContainer } from "./blog.module.css";
import { XMarkIcon } from "@heroicons/react/24/outline";

export interface ChapterLink {
  text: string;
  id: string;
}

interface BlogHeadingsProps extends React.HTMLProps<HTMLDivElement> {
  titles: string[];
  activeLinkIndex: number;
  setActiveLinkIndex: (index: number) => void;
}

const BlogHeadings: React.FC<BlogHeadingsProps> = ({
  titles,
  activeLinkIndex,
  setActiveLinkIndex
}) => {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [linkUnderlineTop, setLinkUnderlineTop] = useState(0);
  const linksRef = useRef<(HTMLElement | null)[]>(Array(titles.length).fill(null));

  const handleLinkClick = (index: number) => {
    setIsBottomSheetOpen(false);
    setTimeout(() => {
      setActiveLinkIndex(index);
    }, 10);
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    function setLinkPosition() {
      timer = setTimeout(() => {
        const currentTab = linksRef.current[activeLinkIndex];
        if (currentTab) {
          setLinkUnderlineTop(currentTab.offsetTop + (currentTab.clientHeight / 2 - 8) ?? 0);
        }
      }, 1);
    }

    setLinkPosition();

    window.addEventListener("scroll", setLinkPosition);

    return () => {
      window.removeEventListener("scroll", setLinkPosition);
      clearTimeout(timer);
    };
  }, [activeLinkIndex, isBottomSheetOpen]);

  return (
    <div className="md:hidden">
      <button
        onClick={() => setIsBottomSheetOpen(true)}
        className="fixed z-10 bottom-6 left-6 w-14 h-14  rounded-full bg-emerald-50 flex justify-center items-center border border-dark shadow-md"
      >
        <SvgIcon name="menu" />
      </button>
      <BottomSheet
        title="Chapters"
        open={isBottomSheetOpen}
        closeModal={() => setIsBottomSheetOpen(false)}
      >
        <div className="flex min-h-[220px] mb-32 relative border-l border-dark pl-10">
          <span
            className="bg-primary rounded-full block w-4 h-4 top-2 left-2 absolute will-change-transform transition-all duration-300"
            style={{ top: linkUnderlineTop }}
          />
          <ul className="flex flex-col grow">
            {titles.map((title, i) => (
              <li
                key={i}
                ref={(el) => (linksRef.current[i] = el)}
                className={`${pl} ${chaptersLinkContainer} ${
                  activeLinkIndex === i ? "text-primary" : ""
                }`}
              >
                <button
                  className="block w-full h-full text-left"
                  onClick={() => handleLinkClick(i)}
                >
                  {title}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="fixed inset-x-0 bottom-0 bg-emerald-50 flex justify-center items-center h-20 border-t border-dark -mx-8 mt-8">
          <button
            type="button"
            className="relative rounded-md bg-green-50 text-gray-400 hover:text-gray-500 focus:outline-none "
            onClick={() => setIsBottomSheetOpen(false)}
          >
            <span className="absolute -inset-2.5" />
            <span className="sr-only">Close panel</span>
            <XMarkIcon className="h-6 w-6 text-dark" aria-hidden="true" />
          </button>
        </div>
      </BottomSheet>
    </div>
  );
};

export default BlogHeadings;
